import { inject as service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { A } from '@ember/array';
import { tracked } from '@glimmer/tracking';
import { next } from '@ember/runloop';
import EmberObject, { action, computed } from '@ember/object';

import EmojiUtils from 'mewe/utils/emoji-utils';
import StorageUtils from 'mewe/shared/storage';
import Scrolling from 'mewe/utils/scrolling-utils';
import PurchasesStore from 'mewe/stores/purchases-store';
import PS from 'mewe/utils/pubsub';
import { reposition } from 'mewe/utils/popup-utils';
import { chunk } from 'lodash';
import Verbose from 'mewe/utils/verbose';
import PopupOpener from 'mewe/pods/components/basic-ui/mw-popup/base/component';
import layout from './template.hbs';
import './styles.scss';

const verbose = Verbose({ prefix: '[Emoji Picker]' }).log;

const ACTIVE = ['active', 'color-app'];
const HIDDEN = 'hidden';
const RECENT = 'including-recent';
const KEY_ESC = 27;
const KEY_TAB = 9;
const ROW_HEIGHT = 46;
const ROWS_ON_PAGE = 6;
const EMOJI_IN_ROW = 9;
const POSITION_OFFSET = 150;

const DEFAULT_ICONS = (tone = '') => {
  return A([
    EmojiUtils.emoji(`:slight_smile:`),
    EmojiUtils.emoji(`:joy:`),
    EmojiUtils.emoji(`:star_struck:`),
    EmojiUtils.emoji(`:thumbsdown${tone}:`),
    EmojiUtils.emoji(`:thumbsup${tone}:`),
    EmojiUtils.emoji(`:heart:`),
  ]);
};

const CATEGORIES = [
  {
    title: __('Recently Used'),
    name: 'recent',
    class: 'emoji-category_recent',
  },
  {
    title: __('Purchased'),
    name: 'purchased',
    class: 'emoji-category_purchased',
  },
  {
    title: __('Smileys & People'),
    name: 'people',
    class: 'emoji-category_people',
  },
  {
    title: __('Animals & Nature'),
    name: 'nature',
    class: 'emoji-category_nature',
  },
  {
    title: __('Food & Drink'),
    name: 'food',
    class: 'emoji-category_food',
  },
  {
    title: __('Activity'),
    name: 'activity',
    class: 'emoji-category_activity',
  },
  {
    title: __('Travel & Places'),
    name: 'travel',
    class: 'emoji-category_travel',
  },
  {
    title: __('Objects'),
    name: 'objects',
    class: 'emoji-category_objects',
  },
  {
    title: __('Symbols'),
    name: 'symbols',
    class: 'emoji-category_symbols',
  },
  {
    title: __('Custom'),
    name: 'custom',
    class: 'emoji-category_custom',
  },
  {
    title: __('Flags'),
    name: 'flags',
    class: 'emoji-category_flags',
  },
];

let buildThresholdList = () => {
  let thresholds = [];
  let numSteps = 10;

  for (let i = 1.0; i <= numSteps; i++) {
    let ratio = i / numSteps;
    thresholds.push(ratio);
  }

  thresholds.push(0);
  return thresholds;
};

let intersectCallback = (rows) => (entries) => {
  entries.forEach((entry) => {
    if (entry.intersectionRatio > 0) {
      let index = parseInt(entry.target.id.replace('erow-', ''));

      for (let i = -ROWS_ON_PAGE; i < ROWS_ON_PAGE * 2; i++)
        if (index + i >= 0 && rows[index + i]) rows[index + i].set('visible', true);
    }
  });
};

let buildRows = (categories, emojiByCategory) => {
  let rows = A();

  let categoryHeaderRows = {};

  let innerHeight = 0;

  verbose('categories');
  verbose(categories);
  verbose('emojiByCategory');
  verbose(emojiByCategory);

  categories
    .map((c) => c.name)
    .forEach((category, ci) => {
      verbose(category);
      if (!emojiByCategory[category]) return;

      let emojiFiltered = emojiByCategory[category].filter((el) => el && !el.duplicate);

      const chunks = chunk(emojiFiltered, EMOJI_IN_ROW);

      if (chunks.length) {
        rows.push(
          EmberObject.create({
            visible: ci == 0 ? true : false,
            title: category,
          })
        );

        categoryHeaderRows[category] = rows.length;

        innerHeight += ROW_HEIGHT;

        chunks.forEach((chunk, ri) => {
          rows.push(
            EmberObject.create({
              visible: ci == 0 && ri < ROWS_ON_PAGE ? true : false,
              emojis: chunk,
              category: category,
              noTone: category === 'recent', // in recent emojis we need to show original tone, not current set SG-22664
            })
          );

          innerHeight += ROW_HEIGHT;
        });
      }
    });

  return { rows, categoryHeaderRows, innerHeight };
};

let setIntersectionObservers = (rows) => {
  let list = buildThresholdList();

  let options = {
    root: document.querySelector('#inner-emojis'),
    threshold: list,
  };

  let observer = new IntersectionObserver(intersectCallback(rows), options);

  next(() =>
    rows.forEach((row, i) => {
      if (i % 6 == 0) {
        const el = document.getElementById(`erow-${i}`);
        if (el) observer.observe(el);
      }
    })
  );
};

export default class MwEmojiPicker extends PopupOpener {
  layout = layout;

  scrolling = Scrolling();
  popupType = 'mw-emoji-picker';
  dropdownId = 'emoji-picker';

  closeOnScroll = false;
  closeOnHoverOutside = false;
  closeOnClickOutside = true;

  @service router;
  @service pickers;
  @service purchase;

  @tracked showPopup = true;

  @tracked baseElement;
  @tracked recent = A();
  @tracked icons = {};
  @tracked defaultIcons = {};
  @tracked searchPhrase = '';
  @tracked searchTimeout = null;
  @tracked searchResults = A();
  @tracked categories = A(CATEGORIES)

  @tracked innerHeight;
  @tracked allRows;
  @tracked categoryHeaderRows;
  @tracked purchased;
  @tracked hasPremium;

  constructor() {
    super(...arguments);

    if (EmojiUtils.isHashMapReady()) {
      this.setIcons();

      PS.Sub('emoji.hashmap.change', () => {
        this.rerender();
      });
    } else {
      PS.Sub('emoji.hashmap.ready', () => {
        this.setIcons();

        PS.Sub('emoji.hashmap.change', () => {
          this.rerender();
        });
      });
    }
  }

  @action
  onInsertElement(element) {
    this.baseElement = element;
    this.open();

    this.closeOnEscBind = this.closeOnEsc.bind(this);
    document.addEventListener('keydown', this.closeOnEscBind);
  }

  @action
  onDestroy() {
    if (this.closeOnEscBind) {
      document.removeEventListener('keydown', this.closeOnEscBind);
    }

    this.scrolling.unbindScrollDown(this.getDialogWrapper());
  }

  setIcons() {
    const emojiByCategory = EmojiUtils.emojiByCategory();

    const recent = this.loadRecentIcons();

    if (recent) emojiByCategory.recent = recent;

    const skin = this.selectedSkinTone,
      thumbTone = skin ? `_tone${skin}` : '';

    const { rows, innerHeight, categoryHeaderRows } = buildRows(this.categories, emojiByCategory);

    this.innerHeight = htmlSafe(`height: ${innerHeight}px`);
    this.allRows = rows;
    this.categoryHeaderRows = categoryHeaderRows;
    this.icons = emojiByCategory;
    this.recent = recent;
    this.defaultIcons = DEFAULT_ICONS(thumbTone);

    if (emojiByCategory.purchased.length) this.purchased = true;

    setIntersectionObservers(rows);
  }

  // call only when necessary because it reloads all icons
  // needed e.g. when skin tone was changed
  rerender() {
    this.icons = {};
    this.defaultIcons = A();
    this.searchResults = A();

    // reload search results after icons were reloaded
    this.doSearch();

    next(() => this.setIcons());
  }

  closeOnEsc(e) {
    if (e.keyCode === KEY_ESC || e.keyCode === KEY_TAB) {
      this.close();
    }
  }

  get popupSize() {
    return {
      width: 440,
      height: 461,
    };
  }

  get selectedSkinTone() {
    return EmojiUtils.get('selectedSkinTone');
  }

  set selectedSkinTone(value) {
    EmojiUtils.set('selectedSkinTone', value);
  }

  get isSearching() {
    return this.searchPhrase?.length > 1;
  }

  get isSearchingLoading() {
    return this.searchPhrase?.length > 1 && !this.searchResults;
  }

  @computed('purchased', 'recent.length')
  get calculatedClass() {
    let classes = 'emoji-tabs';

    if (this.recent.length) {
      classes += ' including-recent';
    }

    if (this.purchased) {
      classes += ' including-purchased';
    }

    return htmlSafe(classes);
  }

  @action
  changeTabOnScroll() {
    const emojisListEl = this.baseElement.querySelector('.inner-emojis');

    if (this.searchPhrase.length) {
      this.baseElement.querySelector('.emoji-tabs .active').classList.remove(ACTIVE);
      return;
    }

    const position = {
      people: emojisListEl.querySelector(`#erow-${this.categoryHeaderRows.people}`).offsetTop - POSITION_OFFSET,
      nature: emojisListEl.querySelector(`#erow-${this.categoryHeaderRows.nature}`).offsetTop - POSITION_OFFSET,
      food: emojisListEl.querySelector(`#erow-${this.categoryHeaderRows.food}`).offsetTop - POSITION_OFFSET,
      activity: emojisListEl.querySelector(`#erow-${this.categoryHeaderRows.activity}`).offsetTop - POSITION_OFFSET,
      travel: emojisListEl.querySelector(`#erow-${this.categoryHeaderRows.travel}`).offsetTop - POSITION_OFFSET,
      objects: emojisListEl.querySelector(`#erow-${this.categoryHeaderRows.objects}`).offsetTop - POSITION_OFFSET,
      symbols: emojisListEl.querySelector(`#erow-${this.categoryHeaderRows.symbols}`).offsetTop - POSITION_OFFSET,
      flags: emojisListEl.querySelector(`#erow-${this.categoryHeaderRows.flags}`).offsetTop - POSITION_OFFSET,
    };

    // purchased/custom/recent category doesn't have to be visible/present
    const purchasedListEl = emojisListEl.querySelector(`h1[name='purchased']`),
      customListEl = emojisListEl.querySelector(`h1[name='custom']`),
      recentListEl = emojisListEl.querySelector(`h1[name='recent']`);

    if (recentListEl) {
      position.recent = recentListEl.offsetTop - POSITION_OFFSET;
    }

    if (purchasedListEl) {
      position.purchased = purchasedListEl.offsetTop - POSITION_OFFSET;
    } else if (customListEl) {
      position.custom = customListEl.offsetTop - POSITION_OFFSET;
    }

    const pos = emojisListEl.scrollTop;

    if (pos < position.people) {
      if (!position.purchased && position.recent) {
        this.highlightTab('recent');
      } else if (position.purchased && pos < position.purchased && position.recent) {
        this.highlightTab('recent');
      } else {
        this.highlightTab('purchased');
      }
    } else if (pos > position.people && pos < position.nature) {
      this.highlightTab('people');
    } else if (pos > position.nature && pos < position.food) {
      this.highlightTab('nature');
    } else if (pos > position.food && pos < position.activity) {
      this.highlightTab('food');
    } else if (pos > position.activity && pos < position.travel) {
      this.highlightTab('activity');
    } else if (pos > position.travel && pos < position.objects) {
      this.highlightTab('travel');
    } else if (pos > position.objects && pos < position.symbols) {
      this.highlightTab('objects');
    } else if (pos > position.symbols && pos < position.custom) {
      this.highlightTab('symbols');
    } else if (pos > position.custom && pos < position.flags) {
      this.highlightTab('custom');
    } else if (pos > position.flags) {
      this.highlightTab('flags');
    }
  }

  highlightTab(className) {
    const emojiTabsEl = this.baseElement.querySelector('.emoji-tabs'),
      tabEl = emojiTabsEl.querySelector(`.${className}`),
      activeEl = emojiTabsEl.querySelector('.active');

    if (activeEl) activeEl.classList.remove(...ACTIVE);
    if (tabEl) tabEl.classList.add(...ACTIVE);
  }

  loadRecentIcons() {
    let emojiStorage = StorageUtils.get(StorageUtils.keys.latestEmojiKey),
      emojisFromStorage = A();

    if (emojiStorage !== null && emojiStorage !== undefined) {
      emojisFromStorage = emojiStorage.split(',');

      let emojis = A();

      if (emojisFromStorage && emojisFromStorage.length) {
        emojisFromStorage.forEach((el) => {
          emojis.push(EmojiUtils.emoji(el));
        });
      }

      return emojis;
    } else {
      return A();
    }
  }

  addEmojiToStorage(emojiShortname, emojiStorage) {
    if (!emojiStorage) return;

    let emojisFromStorage = emojiStorage.split(','),
      emojisToLS = '';

    if (emojisFromStorage.indexOf(emojiShortname) > -1) {
      emojisFromStorage.splice(emojisFromStorage.indexOf(emojiShortname), 1);
    }

    emojisFromStorage.unshift(emojiShortname);

    if (emojisFromStorage.length > this.recentLimit) {
      emojisFromStorage.splice(this.recentLimit + 1, emojisFromStorage.length - this.recentLimit);
    }
    emojisToLS = emojisFromStorage.join(',');
    StorageUtils.set(StorageUtils.keys.latestEmojiKey, emojisToLS);
  }

  @action
  clearSearch() {
    // after clearing search the close button from search input is no longer in DOM
    // and the popup would be closed by that click outside event. To prevent that
    // we postpone clearing searchPhrase
    next(() => {
      this.searchResults = A();
      this.searchPhrase = '';
    });
  }

  @action
  doSearch(searchPhrase = '') {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }

    // don't search for single character
    if (searchPhrase.length === 1) {
      this.searchResults = A();
      return;
    }

    if (searchPhrase === '') {
      this.searchResults = A();
      this.searchPhrase = '';
    } else {
      const searchEmoji = () => {
        this.searchResults = EmojiUtils.emojiByTerm(searchPhrase);
      };
      this.searchTimeout = setTimeout(searchEmoji, 500);
    }
  }

  getDialogWrapper() {
    return document.querySelector('.dialog_wrapper');
  }

  getPlacement() {
    const placement = reposition(this.args.params.parent, this.popupSize);
    // if there is no premium then there is additional banner on top of the emoji picker
    // so we need to move the picker up by 60px
    if (placement.placement === 'top' && this.hasPremium) {
      placement.top = placement.top + 60;
    }

    // there is an offset in some cases (e.g. small chat) that needs to be balanced
    placement.left += 60;

    return placement;
  }

  @action
  changeTab(name) {
    if (!EmojiUtils.isHashMapReady()) return;

    let callback = () => {
      if (this.isDestroyed || this.isDestroying) return;

      let emojisListEl = this.baseElement.querySelector('.inner-emojis'),
        position = emojisListEl.scrollTop;

      this.highlightTab(name);

      const firstEmojiEl = emojisListEl.querySelector(`#erow-${this.categoryHeaderRows[name]}`);

      if (firstEmojiEl) {
        let firstEmojiPosTop = firstEmojiEl.offsetTop;
        let experimentalOffset = 0;

        if (firstEmojiPosTop) {
          position = firstEmojiPosTop - POSITION_OFFSET + experimentalOffset;
        }
      }

      emojisListEl.scrollTop = position;
    };

    if (this.searchPhrase) {
      this.doSearch(); // empty params will clear search
      this.setIcons();
      next(() => callback());
    } else {
      callback();
    }
  }

  @action
  addEmoji(emoji) {
    if (!EmojiUtils.isHashMapReady()) return;

    let tone = this.selectedSkinTone;

    if (
      tone > 0 &&
      emoji.shortname.indexOf('_tone') == -1 &&
      (emoji.diversities || (emoji.keywords || []).includes('diversity'))
    ) {
      emoji = EmojiUtils.skins()[emoji.shortname][tone - 1];
    }

    this.args.params.setEmoji(emoji);

    // adding to localstorage part
    let emojiStorage = StorageUtils.get(StorageUtils.keys.latestEmojiKey);
    if (emojiStorage === null) {
      // create localstorage item and add emoji to it
      StorageUtils.set(StorageUtils.keys.latestEmojiKey, emoji.shortname);
    } else {
      // add emoji as first and remove redundant ones
      this.addEmojiToStorage(emoji.shortname, emojiStorage);
    }

    this.close();
  }

  @action
  selectSkin(number) {
    if (!EmojiUtils.isHashMapReady()) return;

    if (this.selectedSkinTone !== number) {
      this.selectedSkinTone = number;

      if (number > 0) {
        const thumbTone = `_tone${number}`;

        this.searchPhrase = 'diversity';
        this.defaultIcons = DEFAULT_ICONS(thumbTone);

        this.searchResults = EmojiUtils.emojiByTerm(this.searchPhrase);
      } else {
        this.defaultIcons = DEFAULT_ICONS();

        if (this.searchPhrase == 'diversity') {
          this.clearSearch();
        }
      }
    }
  }

  @action
  open() {
    this.purchase.getPurchased().then((response) => {
      if (this.isDestroying || this.isDestroyed) return;
      this.hasPremium = PurchasesStore.getState().hasPremium;
      // users without premium have additional banner on top of the emoji picker
      // so after checking subscription we need to recalculate the position of the picker
      this.position();
    });

    this.pickers.open({ name: 'emoji' });

    if (this.getDialogWrapper()) {
      this.scrolling.bindScrollTreshold(
        this.getDialogWrapper(),
        () => {
          this.close();
        },
        10,
        'sticker-picker-close'
      );
    }

    const emojiStorage = StorageUtils.get(StorageUtils.keys.latestEmojiKey);

    if (emojiStorage === null && !this.baseElement.querySelector('.emoji-tabs .active')) {
      if (this.purchased) {
        this.baseElement.querySelector('.emoji-tabs .purchased').classList.add(...ACTIVE);
      } else {
        this.baseElement.querySelector('.emoji-tabs .people').classList.add(...ACTIVE);
      }
    } else if (this.recent && this.recent.length && !this.baseElement.querySelector('.emoji-tabs .active')) {
      const recentEl = this.baseElement.querySelector('.emoji-tabs .recent');
      recentEl.classList.add(...ACTIVE);
      recentEl.classList.remove(HIDDEN);
      this.baseElement.querySelector('.emoji-tabs').classList.add(RECENT);
    }

    next(() => {
      this.baseElement.querySelector('.emoji-picker-search .h-input_search').focus();
    });
  }

  @action
  goToStore() {
    this.close();

    const doRedirect = () => {
      this.router.transitionTo('app.store.emojis');
    };

    if (this.args.params.beforeStoreRedirection) {
      this.args.params.beforeStoreRedirection(doRedirect);
    } else {
      doRedirect();
    }
  }
}
